'use client'

import type { ReactNode } from 'react'
import type { Bi } from '@/sanskrit/lib/sanskrit/types'
import type { Progress } from '@/sanskrit/lib/progress/engine'
import type { Settings } from '@/sanskrit/lib/settings'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { MotionConfig } from 'motion/react'
import { countXp, emptyProgress, reviveProgress, xpKey } from '@/sanskrit/lib/progress/engine'
import { hasProgress, mergeProgress } from '@/sanskrit/lib/progress/merge'
import { useSession } from '@/lib/client/session'

import {
    DEFAULT_SETTINGS,
    PROGRESS_KEY,
    SETTINGS_KEY,
    reviveSettings
} from '@/sanskrit/lib/settings'

/** Where the Sanskrit progress stands: only on this device (guest), being saved, saved to the account, or not saved yet. */
export type SaveState = 'device' | 'saving' | 'saved' | 'error'

interface AppState {
    hydrated: boolean
    settings: Settings
    updateSettings: (patch: Partial<Settings>) => void
    progress: Progress
    updateProgress: (fn: (p: Progress) => Progress) => void
    resetProgress: () => void
    replaceProgress: (p: Progress) => void
    saveState: SaveState
    /** Try to save again now (after a failed save). */
    retrySave: () => void
}

const AppStateContext = createContext<AppState | null>(null)

const readJson = (key: string): unknown => {
    try {
        const raw = window.localStorage.getItem(key)
        return raw ? JSON.parse(raw) : null
    }
    catch {
        return null
    }
}

const writeJson = (key: string, value: unknown) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    }
    catch {
        // Storage can be full or blocked (private mode); the app keeps working in memory.
    }
}

/** Guests keep their progress under PROGRESS_KEY; every account has its own copy, so a shared browser never mixes them. */
const keyFor = (userId: string | null): string => (userId ? `${PROGRESS_KEY}:${userId}` : PROGRESS_KEY)

const SAVE_DELAY = 1500
const RETRY_DELAY = 15000

/** This browser's own id, so the XP it earns is counted separately from other devices' (see xpKey). */
const DEVICE_KEY = 'vani:device'

const readDevice = (): string => {
    try {
        const stored = window.localStorage.getItem(DEVICE_KEY)

        if (stored) return stored

        const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

        window.localStorage.setItem(DEVICE_KEY, id)

        return id
    }
    catch {
        return ''
    }
}

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
    const { user, loading } = useSession()
    const userId = user?.id ?? null
    const [hydrated, setHydrated] = useState(false)
    const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)
    const [progress, setProgress] = useState<Progress>(emptyProgress)
    const [saveState, setSaveState] = useState<SaveState>('device')
    const settingsRef = useRef<Settings>(DEFAULT_SETTINGS)
    const progressRef = useRef<Progress>(progress)
    const keyRef = useRef<string>(PROGRESS_KEY)
    const userRef = useRef<string | null>(null)
    const deviceRef = useRef<string>('')
    const replaceRef = useRef(false)
    const timer = useRef<number | null>(null)

    const apply = useCallback((p: Progress) => {
        progressRef.current = p
        setProgress(p)
        writeJson(keyRef.current, p)
    }, [])

    // The account's copy is merged on the server with what is already stored, so a save never drops progress made
    // on another device; the merged copy comes back and is merged into this one.
    const save = useCallback(async () => {
        const owner = userRef.current

        if (!owner) return

        if (timer.current) window.clearTimeout(timer.current)
        timer.current = null
        setSaveState('saving')

        // A reset overwrites the account's copy instead of merging into it, so the old progress does not come back.
        const replace = replaceRef.current

        try {
            const res = await fetch('/api/progress/course', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ course: 'sanskrit', data: progressRef.current, ...(replace ? { replace: true } : {}) })
            })

            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const body = await res.json() as { data?: unknown }

            if (replace) replaceRef.current = false
            if (userRef.current !== owner) return
            if (body.data) apply(mergeProgress(progressRef.current, reviveProgress(body.data)))

            setSaveState('saved')
        }
        catch (error) {
            console.error('[sanskrit progress] not saved yet', error instanceof Error ? error.message : error)

            if (userRef.current !== owner) return

            setSaveState('error')
            timer.current = window.setTimeout(() => { void save() }, RETRY_DELAY)
        }
    }, [apply])

    const scheduleSave = useCallback(() => {
        if (!userRef.current) return

        if (timer.current) window.clearTimeout(timer.current)
        setSaveState('saving')
        timer.current = window.setTimeout(() => { void save() }, SAVE_DELAY)
    }, [save])

    useEffect(() => {
        deviceRef.current = readDevice()

        const s = reviveSettings(readJson(SETTINGS_KEY))
        settingsRef.current = s
        setSettings(s)

        const onStorage = (e: StorageEvent) => {
            if (e.key === SETTINGS_KEY) {
                const next = reviveSettings(readJson(SETTINGS_KEY))
                settingsRef.current = next
                setSettings(next)
            }

            if (e.key === keyRef.current) {
                const next = reviveProgress(readJson(keyRef.current))
                progressRef.current = next
                setProgress(next)
            }
        }

        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [])

    // Progress loads once the session is known: a guest's from this device, a learner's from their account (merged
    // with this device's copy of it, and, the first time they sign in here, with what they did as a guest).
    useEffect(() => {
        if (loading) return

        let cancelled = false

        if (timer.current) window.clearTimeout(timer.current)
        timer.current = null
        userRef.current = userId
        keyRef.current = keyFor(userId)
        replaceRef.current = false

        const local = reviveProgress(readJson(keyRef.current))
        progressRef.current = local
        setProgress(local)
        setHydrated(true)

        if (!userId) {
            setSaveState('device')
            return
        }

        setSaveState('saving')

        const load = async () => {
            try {
                const res = await fetch('/api/progress/course?course=sanskrit', { cache: 'no-store' })

                if (!res.ok) throw new Error(`HTTP ${res.status}`)

                const body = await res.json() as { data?: unknown }

                if (cancelled) return

                const stored = body.data ? reviveProgress(body.data) : emptyProgress()
                let merged = mergeProgress(stored, progressRef.current)
                const guest = reviveProgress(readJson(PROGRESS_KEY))

                if (hasProgress(guest)) {
                    merged = mergeProgress(merged, guest)

                    try {
                        window.localStorage.removeItem(PROGRESS_KEY)
                    }
                    catch {
                        // Blocked storage: the guest copy merges again next time, which changes nothing.
                    }
                }

                apply(merged)

                if (JSON.stringify(merged) !== JSON.stringify(stored)) void save()
                else setSaveState('saved')
            }
            catch (error) {
                console.error('[sanskrit progress] the account copy could not load', error instanceof Error ? error.message : error)

                if (cancelled) return

                setSaveState('error')
                timer.current = window.setTimeout(() => { void save() }, RETRY_DELAY)
            }
        }

        void load()

        return () => {
            cancelled = true
        }
    }, [loading, userId, apply, save])

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current)
    }, [])

    // Kids / Adults, explanations, transliteration, text size, motion and the voice come from the shared Suvaani
    // preferences (SanskritShell copies them into these settings; the preferences put them on <html>).

    const updateSettings = useCallback((patch: Partial<Settings>) => {
        const next = { ...settingsRef.current, ...patch }
        settingsRef.current = next
        setSettings(next)
        writeJson(SETTINGS_KEY, next)
    }, [])

    const updateProgress = useCallback((fn: (p: Progress) => Progress) => {
        const prev = progressRef.current
        const next = fn(prev)
        if (next === prev) return
        apply(countXp(prev, next, deviceRef.current ? xpKey(deviceRef.current, userRef.current) : ''))
        scheduleSave()
    }, [apply, scheduleSave])

    const replaceProgress = useCallback((p: Progress) => {
        replaceRef.current = true
        apply(p)
        scheduleSave()
    }, [apply, scheduleSave])

    const resetProgress = useCallback(() => replaceProgress(emptyProgress()), [replaceProgress])

    const retrySave = useCallback(() => { void save() }, [save])

    const value = useMemo<AppState>(() => ({
        hydrated,
        settings,
        updateSettings,
        progress,
        updateProgress,
        resetProgress,
        replaceProgress,
        saveState,
        retrySave
    }), [hydrated, settings, updateSettings, progress, updateProgress, resetProgress, replaceProgress, saveState, retrySave])

    const reducedMotion = settings.motion === 'reduce' ? 'always' : settings.motion === 'full' ? 'never' : 'user'

    return (
        <AppStateContext.Provider value={value}>
            <MotionConfig reducedMotion={reducedMotion}>
                {children}
            </MotionConfig>
        </AppStateContext.Provider>
    )
}

export const useAppState = (): AppState => {
    const ctx = useContext(AppStateContext)
    if (!ctx) throw new Error('useAppState must be used inside AppStateProvider')
    return ctx
}

export const useSettings = () => {
    const { settings, updateSettings, hydrated } = useAppState()
    return { settings, updateSettings, hydrated }
}

export const useProgress = () => {
    const { progress, updateProgress, resetProgress, replaceProgress, hydrated } = useAppState()
    return { progress, updateProgress, resetProgress, replaceProgress, hydrated }
}

/** Pick the explanation language for a bilingual string. */
export const useT = () => {
    const { settings } = useAppState()
    const lang = settings.lang

    return useCallback((text: Bi | string | undefined | null): string => {
        if (!text) return ''
        if (typeof text === 'string') return text
        return text[lang] || text.en
    }, [lang])
}

export const useLang = () => useAppState().settings.lang

export const useIsKids = () => useAppState().settings.mode === 'kids'
